import JsPDF from "jspdf";
import axios from "axios";
const { AwesomeQR } = require("awesome-qr");

// Helper function to capitalize first letter
function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/** first + optional middle + last (parent / teacher / student slices) */
function formatDisplayName(person) {
  if (!person) return "";
  const rawMid = person.middleName ?? person.middle_name;
  const fn = person.firstName
    ? capitalizeFirstLetter(String(person.firstName).trim())
    : "";
  const mid =
    rawMid && String(rawMid).trim() !== ""
      ? capitalizeFirstLetter(String(rawMid).trim()) + " "
      : "";
  const ln = person.lastName
    ? capitalizeFirstLetter(String(person.lastName).trim())
    : "";
  return `${fn} ${mid}${ln}`.replace(/\s+/g, " ").trim();
}

// Helper function to resize and compress image
async function resizeImage(
  base64Image,
  maxWidth = 200,
  maxHeight = 200,
  quality = 0.7,
  imageName = "image"
) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to base64 with compression
      const resizedBase64 = canvas.toDataURL("image/jpeg", quality);

      resolve(resizedBase64);
    };
    img.onerror = (error) => {
      reject(error);
    };
    img.src = base64Image;
  });
}

// Function to add a new page with sections
function addPageWithSections(pdf, index) {
  if (index > 0 && index % 4 === 0) {
    pdf.addPage();
  }
  // Define the page size (A4)
  const pageWidth = 210;
  const pageHeight = 297;

  // Define section dimensions
  const sectionWidth = pageWidth / 2;
  const sectionHeight = pageHeight / 2;
  // Draw section borders on the new page
  pdf.rect(0, 0, sectionWidth, sectionHeight);
  pdf.rect(sectionWidth, 0, sectionWidth, sectionHeight);
  pdf.rect(0, sectionHeight, sectionWidth, sectionHeight);
  pdf.rect(sectionWidth, sectionHeight, sectionWidth, sectionHeight);
}

// Function to draw parent header
function drawParentHeader(
  pdf,
  user,
  index,
  imgData,
  t,
  locale,
  isArabic = false
) {
  pdf.setPage(Math.floor(index / 4) + 1);
  // Define the page size (A4)
  const pageWidth = 210;
  const pageHeight = 297;

  // Define section dimensions
  const sectionWidth = pageWidth / 2;
  const sectionHeight = pageHeight / 2;
  // Calculate section coordinates
  const x = (index % 2) * sectionWidth;
  const y = (Math.floor(index / 2) % 2) * sectionHeight;

  // Get current date with French formatting
  const date = new Date();
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  // Add building logo (left side for LTR, right side for RTL)
  const logoWidth = 12;
  const logoHeight = 12;
  if (imgData) {
    const logoX = isArabic ? x + sectionWidth - logoWidth - 5 : x + 5;
    pdf.addImage(imgData, "JPEG", logoX, y + 2, logoWidth, logoHeight);
  }

  // Add date (right side for LTR, left side for RTL)
  pdf.setFontSize(6);
  const dateX = isArabic ? x + 5 : x + sectionWidth - 5;
  const dateAlign = isArabic ? "left" : "right";
  pdf.text(dateX, y + 8, date.toLocaleDateString(locale, options), dateAlign);

  pdf.setFont("Amiri", "Bold");
  pdf.setFontSize(12);

  // Center the title
  const titleText = t("pdfLogin.loginInfo");
  const titleWidth = pdf.getTextWidth(titleText);
  const titleX = x + (sectionWidth - titleWidth) / 2;
  pdf.text(titleX, y + 14, titleText);
  pdf.setFont("Amiri", "normal");
  pdf.setFontSize(9);

  // Add parent name below title
  const namePart = formatDisplayName(user);
  let parentName = "";
  if (user.type === "father") {
    parentName = `${t("pdfLogin.father")} : ${namePart}`;
  } else if (user.type === "mother") {
    parentName = `${t("pdfLogin.mother")} : ${namePart}`;
  } else if (user.type === "guardian") {
    parentName = `${t("pdfLogin.guardian")} : ${namePart}`;
  }

  const parentNameWidth = pdf.getTextWidth(parentName);
  const parentNameX = x + (sectionWidth - parentNameWidth) / 2;
  pdf.text(parentNameX, y + 20, parentName);

  // Add students names below parent name (prefer structured names for middleName)
  const studentPdfName = (s) => {
    if (!s) return "";
    if (s.firstName || s.lastName || s.middleName || s.middle_name)
      return formatDisplayName(s);
    return (s.fullName || "").trim();
  };
  let students = user.students
    .map((s) => {
      const name = studentPdfName(s);
      if (s.classRoom && s.classRoom.name)
        return name + " (" + s.classRoom.name + ")";
      return name;
    })
    .join("\n");

  const studentLines = students.split("\n");
  const firstStudent = studentLines[0];
  const studentsText = `${t("pdfLogin.students")} : ${firstStudent}`;
  const studentsWidth = pdf.getTextWidth(studentsText);
  const studentsX = x + (sectionWidth - studentsWidth) / 2;
  pdf.text(studentsX, y + 25, studentsText);

  // Add additional students below with same alignment
  studentLines.slice(1).forEach((student, index) => {
    const studentWidth = pdf.getTextWidth(student);
    const studentX = x + (sectionWidth - studentWidth) / 2;
    pdf.text(studentX, y + 30 + index * 4, student);
  });

  // Calculate dynamic Y position based on number of students
  const studentLinesCount = studentLines.length;
  return 31 + studentLinesCount * 4;
}

// Function to draw teacher header
function drawTeacherHeader(
  pdf,
  teacher,
  index,
  imgData,
  t,
  locale,
  isArabic = false
) {
  pdf.setPage(Math.floor(index / 4) + 1);
  // Define the page size (A4)
  const pageWidth = 210;
  const pageHeight = 297;

  // Define section dimensions
  const sectionWidth = pageWidth / 2;
  const sectionHeight = pageHeight / 2;
  // Calculate section coordinates
  const x = (index % 2) * sectionWidth;
  const y = (Math.floor(index / 2) % 2) * sectionHeight;

  // Get current date with French formatting
  const date = new Date();
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  // Add building logo (left side for LTR, right side for RTL)
  const logoWidth = 12;
  const logoHeight = 12;
  if (imgData) {
    const logoX = isArabic ? x + sectionWidth - logoWidth - 5 : x + 5;
    pdf.addImage(imgData, "JPEG", logoX, y + 2, logoWidth, logoHeight);
  }

  // Add date (right side for LTR, left side for RTL)
  pdf.setFontSize(6);
  const dateX = isArabic ? x + 5 : x + sectionWidth - 5;
  const dateAlign = isArabic ? "left" : "right";
  pdf.text(dateX, y + 8, date.toLocaleDateString(locale, options), dateAlign);

  pdf.setFont("Amiri", "Bold");
  pdf.setFontSize(12);

  // Center the title
  const titleText = t("pdfLogin.loginInfo");
  const titleWidth = pdf.getTextWidth(titleText);
  const titleX = x + (sectionWidth - titleWidth) / 2;
  pdf.text(titleX, y + 14, titleText);
  pdf.setFont("Amiri", "normal");
  pdf.setFontSize(9);

  // Add teacher name below title (API may nest under employee)
  const teacherName = `${t("pdfLogin.teacher")} : ${formatDisplayName(
    teacher.employee || teacher
  )}`;

  const teacherNameWidth = pdf.getTextWidth(teacherName);
  const teacherNameX = x + (sectionWidth - teacherNameWidth) / 2;
  pdf.text(teacherNameX, y + 20, teacherName);

  // Return dynamic Y position (no students for teachers)
  return 35;
}

// Function to draw student header
function drawStudentHeader(
  pdf,
  student,
  index,
  imgData,
  t,
  locale,
  isArabic = false
) {
  pdf.setPage(Math.floor(index / 4) + 1);
  // Define the page size (A4)
  const pageWidth = 210;
  const pageHeight = 297;

  // Define section dimensions
  const sectionWidth = pageWidth / 2;
  const sectionHeight = pageHeight / 2;
  // Calculate section coordinates
  const x = (index % 2) * sectionWidth;
  const y = (Math.floor(index / 2) % 2) * sectionHeight;

  // Get current date with French formatting
  const date = new Date();
  const options = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  // Add building logo (left side for LTR, right side for RTL)
  const logoWidth = 12;
  const logoHeight = 12;
  if (imgData) {
    const logoX = isArabic ? x + sectionWidth - logoWidth - 5 : x + 5;
    pdf.addImage(imgData, "JPEG", logoX, y + 2, logoWidth, logoHeight);
  }

  // Add date (right side for LTR, left side for RTL)
  pdf.setFontSize(6);
  const dateX = isArabic ? x + 5 : x + sectionWidth - 5;
  const dateAlign = isArabic ? "left" : "right";
  pdf.text(dateX, y + 8, date.toLocaleDateString(locale, options), dateAlign);

  pdf.setFont("Amiri", "Bold");
  pdf.setFontSize(12);

  // Center the title
  const titleText = t("pdfLogin.loginInfo");
  const titleWidth = pdf.getTextWidth(titleText);
  const titleX = x + (sectionWidth - titleWidth) / 2;
  pdf.text(titleX, y + 14, titleText);
  pdf.setFont("Amiri", "normal");
  pdf.setFontSize(9);

  // Add student name below title
  const className = student.classRoom ? ` (${student.classRoom.name})` : "";
  const studentName = `${t("pdfLogin.student")} : ${formatDisplayName(
    student
  )}${className}`;

  const studentNameWidth = pdf.getTextWidth(studentName);
  const studentNameX = x + (sectionWidth - studentNameWidth) / 2;
  pdf.text(studentNameX, y + 20, studentName);

  // Return dynamic Y position (no students for students)
  return 35;
}

function drawAdminHeader(pdf, user, index, imgData, t, locale, isArabic = false) {
  const pageWidth = 210;
  const pageHeight = 297;
  const sectionWidth = pageWidth / 2;
  const sectionHeight = pageHeight / 2;
  const x = (index % 2) * sectionWidth;
  const y = (Math.floor(index / 2) % 2) * sectionHeight;

  pdf.addImage(imgData, "JPEG", x + 10, y + 5, 20, 20);

  pdf.setFontSize(10);
  const title = "EdManager";
  const titleX = x + (sectionWidth - pdf.getTextWidth(title)) / 2;
  pdf.text(titleX, y + 12, title);

  pdf.setFontSize(8);
  const displayName = formatDisplayName(user) || user.userName || "";
  const nameWidth = pdf.getTextWidth(displayName);
  const nameX = x + (sectionWidth - nameWidth) / 2;
  pdf.text(nameX, y + 20, displayName);

  if (user.userName) {
    pdf.setFontSize(7);
    const userLine = user.userName;
    const userX = x + (sectionWidth - pdf.getTextWidth(userLine)) / 2;
    pdf.text(userX, y + 26, userLine);
  }

  return 35;
}

async function drawSection(
  pdf,
  user,
  index,
  logo,
  imgData,
  settings,
  userType = "parent",
  preloadedImages,
  t,
  locale,
  isArabic = false
) {
  let qrCodeError = false;
  // Draw appropriate header based on user type
  let dynamicY;
  if (userType === "teacher") {
    dynamicY = drawTeacherHeader(
      pdf,
      user,
      index,
      imgData,
      t,
      locale,
      isArabic
    );
  } else if (userType === "student") {
    dynamicY = drawStudentHeader(
      pdf,
      user,
      index,
      imgData,
      t,
      locale,
      isArabic
    );
  } else if (userType === "admin") {
    dynamicY = drawAdminHeader(
      pdf,
      user,
      index,
      imgData,
      t,
      locale,
      isArabic
    );
  } else {
    dynamicY = drawParentHeader(pdf, user, index, imgData, t, locale, isArabic);
  }

  //add qrcode image
  const qrData = window.btoa(user.token + "#" + settings.base_url);

  // Get section coordinates (needed for content positioning)
  const pageWidth = 210;
  const pageHeight = 297;
  const sectionWidth = pageWidth / 2;
  const sectionHeight = pageHeight / 2;
  const x = (index % 2) * sectionWidth;
  const y = (Math.floor(index / 2) % 2) * sectionHeight;

  // Section 1: Login Credentials
  pdf.setFontSize(9);
  const option1X = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.option1"))
    : x + 10;
  pdf.text(t("pdfLogin.option1"), option1X, y + dynamicY);

  pdf.setFontSize(8);
  const usernameLabelX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.username"))
    : x + 10;
  pdf.text(t("pdfLogin.username"), usernameLabelX, y + dynamicY + 6);
  pdf.setFontSize(7);
  const usernameValueX = isArabic
    ? x + sectionWidth - 40 - pdf.getTextWidth(user.userName || "")
    : x + 40;
  pdf.text(user.userName || "", usernameValueX, y + dynamicY + 6);

  // Section 2: QR Code Login
  pdf.setFontSize(9);
  const option2X = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.option2"))
    : x + 10;
  pdf.text(t("pdfLogin.option2"), option2X, y + dynamicY + 12);

  pdf.setFontSize(8);
  const scanQRX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.scanQRCode"))
    : x + 10;
  pdf.text(t("pdfLogin.scanQRCode"), scanQRX, y + dynamicY + 18);

  // Add QR code
  const qrCodeSize = 25;
  const qrCodeX = x + (sectionWidth - qrCodeSize) / 2;

  let dataURL;
  try {
    dataURL = await new AwesomeQR({
      text: qrData,
      logoImage: logo,
      size: 200,
      correctLevel: 1,
      logoScale: 0.2,
      logoMargin: 0,
      logoCornerRadius: 8,
      whiteMargin: false,
      dotScale: 1,
      autoColor: true,
      binarize: false,
      binarizeThreshold: 128,
      backgroundColor: "#FFFFFF",
      margin: 3,
    }).draw();
  } catch (error) {
    console.error("Error generating QR code:", error);
    qrCodeError = true;
    return { user, error: true };
  }

  pdf.setPage(Math.floor(index / 4) + 1);
  pdf.addImage(
    dataURL,
    "JPEG",
    qrCodeX,
    y + dynamicY + 22,
    qrCodeSize,
    qrCodeSize
  );

  // Section 3: Access Platform
  pdf.setFontSize(9);
  const howToAccessX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.howToAccess"))
    : x + 10;
  pdf.text(t("pdfLogin.howToAccess"), howToAccessX, y + dynamicY + 53);

  // Web platform text
  pdf.setFontSize(8);
  const webPlatformX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.webPlatform"))
    : x + 10;
  pdf.text(t("pdfLogin.webPlatform"), webPlatformX, y + dynamicY + 59);

  // Get the appropriate URL and app info based on user type
  const platformUrls = {
    parent: "https://parent.edtrust.tn/#",
    student: "https://student.edtrust.tn/#",
    teacher: "https://teacher.edtrust.tn/#",
    admin: settings.base_url || "https://edtrust.tn",
  };
  const platformUrl =
    platformUrls[userType?.toLowerCase()] || "https://parent.edtrust.tn/#";

  const appNames = {
    parent: "EdParent",
    student: "EdStudent",
    teacher: "EdTeacher",
    admin: "EdManager",
  };
  const appName = appNames[userType?.toLowerCase()] || "EdParent";

  // Add the platform URL text
  pdf.setFontSize(7);
  const platformUrlX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(platformUrl)
    : x + 10;
  pdf.text(platformUrl, platformUrlX, y + dynamicY + 63);

  const logoSize = 10;
  const logoX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(platformUrl) - logoSize - 5
    : x + 10 + pdf.getTextWidth(platformUrl) + 5;
  const logoY = y + dynamicY + 56; // Centered with both "Plateforme web :" and URL text

  pdf.addImage(
    preloadedImages.appLogo,
    "JPEG",
    logoX,
    logoY,
    logoSize,
    logoSize
  );

  // Add clickable link over the logo
  pdf.link(logoX, logoY, logoSize, logoSize, {
    url: platformUrl,
  });

  // Application mobile text
  pdf.setFontSize(8);
  const mobileAppX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.mobileApp"))
    : x + 10;
  pdf.text(t("pdfLogin.mobileApp"), mobileAppX, y + dynamicY + 69);
  pdf.setFontSize(7);
  const installAppX = isArabic
    ? x +
      sectionWidth -
      10 -
      pdf.getTextWidth(t("pdfLogin.installApp", { appName }))
    : x + 10;
  pdf.text(
    t("pdfLogin.installApp", { appName }),
    installAppX,
    y + dynamicY + 73
  );

  // Add app store images centered in a row
  const imageWidth = 22;
  const imageHeight = 10;
  const imageSpacing = 2;
  const totalWidth =
    preloadedImages.appStoreImages.length * imageWidth +
    (preloadedImages.appStoreImages.length - 1) * imageSpacing;
  const startX = x + (sectionWidth - totalWidth) / 2;
  const imageY = y + dynamicY + 75;

  // Get app store URLs based on user type
  const appStoreUrlsByType = {
    parent: [
      "https://apps.apple.com/us/app/edparent/id6469570430",
      "https://play.google.com/store/apps/details?id=com.edcontrolpaternal.controlpaternal",
      "https://appgallery.huawei.com/app/C107441097",
    ],
    teacher: [
      "https://apps.apple.com/us/app/edteacher/id6471353124",
      "https://play.google.com/store/apps/details?id=com.edtrust.edteacher",
      "https://appgallery.huawei.com/app/C109015147",
    ],
    student: [
      "https://apps.apple.com/tn/app/edstudent/id6751495124",
      "https://play.google.com/store/apps/details?id=com.edtrust.edstudent",
      "https://appgallery.huawei.com/app/C115080693",
    ],
  };
  const appStoreUrls =
    appStoreUrlsByType[userType?.toLowerCase()] || appStoreUrlsByType.parent;

  preloadedImages.appStoreImages.forEach((appStoreImg, index) => {
    const imageX = startX + index * (imageWidth + imageSpacing);

    // Add the image
    pdf.addImage(appStoreImg, "JPEG", imageX, imageY, imageWidth, imageHeight);

    // Add clickable link over the image
    pdf.link(imageX, imageY, imageWidth, imageHeight, {
      url: appStoreUrls[index],
    });
  });

  // Add important information section
  pdf.setFontSize(8);
  const importantInfoX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.importantInfo"))
    : x + 10;
  pdf.text(t("pdfLogin.importantInfo"), importantInfoX, imageY + 13);

  pdf.setFontSize(6);
  const changePasswordX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.changePassword"))
    : x + 10;
  pdf.text(t("pdfLogin.changePassword"), changePasswordX, imageY + 18);
  const keepDocumentX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.keepDocumentSafe"))
    : x + 10;
  pdf.text(t("pdfLogin.keepDocumentSafe"), keepDocumentX, imageY + 21);
  const contactAdminX = isArabic
    ? x + sectionWidth - 10 - pdf.getTextWidth(t("pdfLogin.contactAdmin"))
    : x + 10;
  pdf.text(t("pdfLogin.contactAdmin"), contactAdminX, imageY + 24);

  return { user, error: qrCodeError };
}

export const generateAllLoginPdf = async ({
  parentsLogins,
  settings,
  userType = "parent", // "parent" or "teacher"
  t, // Translation function
  locale = "fr-FR", // Current locale
}) => {
  const pdf = new JsPDF();
  const qrCodeErrors = [];
  pdf.setFont("Amiri");
  pdf.setFontSize(10);

  const isArabic = locale === "ar-AR";

  let schoolLogo = null;
  let arrayOfImages = settings.image;
  for (var i = 0; i < arrayOfImages.length; i++) {
    if (settings.activeBuilding === arrayOfImages[i].db) {
      var imgSrc = arrayOfImages[i].logo;
      schoolLogo = arrayOfImages[i].logo;
    }
  }

  let imgData = "data:image/jpeg;base64, ";

  await axios
    .get(imgSrc, {
      responseType: "arraybuffer",
    })
    .then((response) => {
      imgData += Buffer.from(response.data, "binary").toString("base64");
    });

  try {
    imgData = await resizeImage(imgData, 200, 200, 0.7, "Building Logo");
  } catch (error) {
    // Keep original if resize fails
  }

  let logo = "data:image/jpeg;base64, ";

  await axios
    .get(schoolLogo || "favicon.png", {
      responseType: "arraybuffer",
    })
    .then((response) => {
      logo += Buffer.from(response.data, "binary").toString("base64");
    });

  const appLogos = {
    parent: "/resources/images/logos/EDparent.jpeg",
    student: "/resources/images/logos/EdStudent.jpeg",
    teacher: "/resources/images/logos/EdTeacher.jpeg",
    admin: "/resources/images/logos/EdManager.jpeg",
  };
  const appLogoPath =
    appLogos[userType?.toLowerCase()] ||
    "/resources/images/logos/EDparent.jpeg";

  // Load app logo as base64
  let appLogoData = "data:image/jpeg;base64,";
  try {
    const appLogoResponse = await axios.get(appLogoPath, {
      responseType: "arraybuffer",
    });
    appLogoData += Buffer.from(appLogoResponse.data, "binary").toString(
      "base64"
    );
  } catch (error) {
    console.error("Error loading app logo:", error);
  }

  // Load app store images as base64
  const appStoreImagePaths = [
    "/resources/images/media/appStore.jpeg",
    "/resources/images/media/googlePlay.jpeg",
    "/resources/images/media/huawei.jpeg",
  ];

  const appStoreImagesData = [];
  for (const imagePath of appStoreImagePaths) {
    let imageData = "data:image/jpeg;base64,";
    try {
      const response = await axios.get(imagePath, {
        responseType: "arraybuffer",
      });
      imageData += Buffer.from(response.data, "binary").toString("base64");
      appStoreImagesData.push(imageData);
    } catch (error) {
      console.error(`Error loading image ${imagePath}:`, error);
      appStoreImagesData.push(imageData); // Push empty base64 to maintain array length
    }
  }

  // Store all preloaded images in one object as base64 data URLs
  const preloadedImages = {
    appLogo: appLogoData,
    appStoreImages: appStoreImagesData,
  };

  let promises = [];
  let allLogins = [];

  if (userType === "parent") {
    parentsLogins.forEach((parent) => {
      if (parent.father) {
        allLogins.push({
          ...parent.father,
          type: "father",
          students: parent.students,
        });
      }
      if (parent.mother) {
        allLogins.push({
          ...parent.mother,
          type: "mother",
          students: parent.students,
        });
      }
      if (parent.guardian) {
        allLogins.push({
          ...parent.guardian,
          type: "guardian",
          students: parent.students,
        });
      }
    });
  } else if (userType === "teacher") {
    // For teachers, the data structure is different
    parentsLogins.forEach((teacher) => {
      allLogins.push({
        ...teacher,
        type: "teacher",
        userName: teacher.email, // Use email as username for teachers
        // Teachers don't have students in this context
      });
    });
  } else if (userType === "student") {
    // For students, the data structure is different
    parentsLogins.forEach((student) => {
      allLogins.push({
        ...student,
        type: "student",
        userName: student.userName || "",
      });
    });
  } else if (userType === "admin") {
    parentsLogins.forEach((adminUser) => {
      allLogins.push({
        ...adminUser,
        type: "admin",
        userName: adminUser.userName || adminUser.email || "",
      });
    });
  }

  // Iterate through user data
  allLogins.forEach((user, index) => {
    // Check if a new page is needed
    addPageWithSections(pdf, index);
    promises.push(
      drawSection(
        pdf,
        user,
        index,
        logo,
        imgData,
        settings,
        userType,
        preloadedImages,
        t,
        locale,
        isArabic
      )
    );
  });

  // Save or display the PDF
  const results = await Promise.all(promises);

  // Check for QR code errors
  results.forEach((result) => {
    if (result && result.error) {
      qrCodeErrors.push(result.user);
    }
  });

  const filename =
    userType === "teacher"
      ? t("pdfLogin.teacherLogins") + ".pdf"
      : userType === "student"
      ? t("pdfLogin.studentLogins") + ".pdf"
      : userType === "admin"
      ? t("pdfLogin.adminLogins") + ".pdf"
      : t("pdfLogin.parentLogins") + ".pdf";
  pdf.save(filename);

  // Return QR code errors for warning message after PDF download
  return { error: false, pdf, qrCodeErrors, userType };
};

export default {
  generateAllLoginPdf,
};
